import React, { useEffect, useRef, useState, useCallback } from 'react';
import Buttons from '../buttons/buttons';
import { RHYBUS_ACCESS_TOKEN } from '../../Configuration';

type Props = {
    message: string;
    onChangeMessage: (msg: string) => void;
    onSend: () => void;
    isLoading: boolean;
    onStop?: () => void;
};

export default function ChatFooter({ message, onChangeMessage, onSend, isLoading, onStop }: Props) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const barsRef = useRef<(HTMLDivElement | null)[]>([]); 

    const isSendDisabled = !message.trim() || isRecording || isProcessing;

    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = "auto"
            const newHeight = Math.min(textarea.scrollHeight, 100)
            textarea.style.height = `${newHeight - 8}px`
        }       
    }, [message])

    // Clean up audio context on unmount
    useEffect(() => {
        return () => {
            stopVisualizer();
        };
    }, []);

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (isSendDisabled) return; 
            onSend();
        }
    };

    // --- VISUALIZER LOGIC ---
    const startVisualizer = (stream: MediaStream) => {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const analyser = audioCtx.createAnalyser();
        const source = audioCtx.createMediaStreamSource(stream);

        source.connect(analyser);
        analyser.fftSize = 64; 
        
        audioContextRef.current = audioCtx;
        analyserRef.current = analyser;
        sourceRef.current = source;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const animate = () => {
            analyser.getByteFrequencyData(dataArray);
            barsRef.current.forEach((bar, index) => {
                if (bar) {
                    const value = dataArray[index] || 0;
                    const height = value > 10 ? Math.max(4, (value / 255) * 24) : 4;
                    bar.style.height = `${height}px`;
                }
            });
            animationFrameRef.current = requestAnimationFrame(animate);
        };
        animate();
    };

    const stopVisualizer = () => {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        if (audioContextRef.current) audioContextRef.current.close();
        audioContextRef.current = null;
        analyserRef.current = null;
        sourceRef.current = null;
    };

    const startRecording = async () => {
        if (isProcessing || isLoading) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            startVisualizer(stream);
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) audioChunksRef.current.push(event.data);
            };
            mediaRecorder.start();
            setIsRecording(true);
        } catch (err) {
            console.error("Error accessing microphone", err);
            alert("Could not access microphone.");
        }
    };

    const cancelRecording = () => {
        stopVisualizer();
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
        setIsRecording(false);
        audioChunksRef.current = [];
    };

    // Wrapped in useCallback to use in useEffect
    const stopAndTranscribe = useCallback(() => {
        if (!mediaRecorderRef.current) return;
        
        stopVisualizer(); 
        setIsProcessing(true); 

        mediaRecorderRef.current.onstop = async () => {
            mediaRecorderRef.current?.stream.getTracks().forEach(track => track.stop());

            const mimeType = mediaRecorderRef.current?.mimeType || ""; 
            let extension = "webm";
            let type = "audio/webm";

                    // 3. Check for specific Browser formats
                    if (mimeType.includes("mp4") || mimeType.includes("aac")) {
                        // iOS and macOS (Safari)
                        extension = "mp4";
                        type = "audio/mp4";
                    } else if (mimeType.includes("mpeg") || mimeType.includes("mp3")) {
                        // Edge case: If a browser/device supports native MP3 recording
                        extension = "mp3";
                        type = "audio/mpeg";
                    } else if (mimeType.includes("ogg")) {
                        // Firefox often uses Ogg
                        extension = "ogg";
                        type = "audio/ogg";
                    } else if (mimeType.includes("wav")) {
                        // Rare, but some configurations might force WAV
                        extension = "wav";
                        type = "audio/wav";
                    }

                    // 4. Create the file with the CORRECT extension
                    const audioBlob = new Blob(audioChunksRef.current, { type: type });
                    const fileName = `recording.${extension}`;
                    const audioFile = new File([audioBlob], fileName, { type: type });

                    // Prepare Form Data
                    const formData = new FormData();
                    formData.append("audio_file", audioFile);

                    try {
                        const token = localStorage.getItem(RHYBUS_ACCESS_TOKEN) || sessionStorage.getItem(RHYBUS_ACCESS_TOKEN) || "";
                        const response = await fetch(`${process.env.REACT_APP_ANEW_NLP_API_URL}/speech/transcribe`, {
                            method: "POST",
                            headers: {
                                'Authorization': `Bearer ${token}`
                            },
                            body: formData
                        });

                if (response.ok) {
                    const data = await response.json();
                    if (data.transcript) {
                        const newText = message ? `${message} ${data.transcript}` : data.transcript;
                        onChangeMessage(newText);
                    }
                }
            } catch (err) {
                console.error("Error sending audio to API", err);
            } finally {
                setIsProcessing(false);
                setIsRecording(false);
            }
        };

        mediaRecorderRef.current.stop();
    }, [message, onChangeMessage]);

    // Global Enter Key Listener for Recording 
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if (isRecording && e.key === 'Enter') {
                e.preventDefault();
                stopAndTranscribe();
            }
        };

        if (isRecording) {
            window.addEventListener('keydown', handleGlobalKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, [isRecording, stopAndTranscribe]);


    const getVoiceButtonClass = () => {
        let classes = 'btn font-0 px-2 justify-content-center btn-border-radius-3 align-items-center lh-normal';
        if (!isRecording && !isProcessing) {
            classes += " text-color-61";
        }
        return classes;
    };

    const getVoiceButtonStyle = () => {
        if (isLoading) return {}; 
        if (isRecording) return { color: '#cccccc' };
        if (isProcessing) return { color: '#333333' };
        return {};
    };

    return (
        <div className={`chat-footer border-1 p-relative ${message.length > 50 ? 'chat-footer-expanded' : ''}`} >
            
            {(isRecording || isProcessing) && (
                <div className="voice-recording-overlay">
                    <div className="d-flex align-items-center gap-2">
                        <span className="icon-voice font-20" style={{color: '#273CC2'}}></span>
                    </div>
                    
                    <div className="voice-visualizer">
                        {Array.from({ length: 30 }).map((_, i) => (
                            <div 
                                key={i} 
                                className="voice-bar"
                                ref={el => barsRef.current[i] = el}
                            ></div>
                        ))}
                    </div>

                    <div className="voice-actions">
                        {!isProcessing && (
                            <button 
                                className="voice-action-btn btn-cancel" 
                                onClick={cancelRecording}
                                title="Cancel"
                            >
                                <span className="icon-rb-close font-12"></span>
                            </button>
                        )}

                        <button 
                            className="voice-action-btn btn-confirm" 
                            onClick={!isProcessing ? stopAndTranscribe : undefined}
                            title="Done"
                            disabled={isProcessing}
                        >
                            {isProcessing ? (
                                <div className="voice-spinner"></div>
                            ) : (
                                <span className="icon-rb-checked font-14"></span>
                            )}
                        </button>
                    </div>
                </div>
            )}

            <div className="d-flex align-items-center gap-2 order-flex">
                <div>
                    <span className="icon-ask-chatbot text-color-61 font-20"></span>
                </div>
                <div className="col textarea">
                    <textarea
                        ref={textareaRef}
                        id="ask-ai-textarea"
                        className="no-border w-100 font-13 ps-1"
                        aria-label="Type your message to Clutch Bot"
                        placeholder={isRecording ? "Listening..." : isProcessing ? "Processing..." : "Ask Clutch Bot..."}
                        value={message}
                        onChange={(e) => onChangeMessage(e.target.value)}
                        onKeyDown={handleKeyPress}
                        disabled={isRecording || isProcessing} 
                    />
                </div>
                <div className="d-flex gap-2">
                    <Buttons
                        label="Voice"
                        ariaLabel="Voice"
                        icon={`icon-voice font-22`}
                        className={`${getVoiceButtonClass()} voice-button`}
                        style={getVoiceButtonStyle()}
                        onClick={startRecording}
                        disabled={isLoading} 
                    />
                    {isLoading ? (
                        <button
                            className="stop-button-wrapper"
                            onClick={onStop}
                            title="Stop Generating"
                            aria-label="Stop Generating"
                        >
                            <span className="stop-button-icon"></span>
                        </button>
                    ) : (
                        <Buttons
                            label="Send"
                            ariaLabel="Send"
                            icon="icon-rb-arrow-line-up-thick font-15 whitetext"
                            className="btn btn-primary px-0 font-0 send-button btn-border-radius-3 sora-semibold whitetext justify-content-center align-items-center"
                            disabled={isSendDisabled}
                            onClick={() => {
                                if (!isSendDisabled) onSend();
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}